import React, {
    useState,
    useContext,
    useRef,
    useEffect,
    useCallback,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Raven from 'raven-js';
import { Portal } from 'react-portal';
// import { Transition } from 'react-transition-group';

import { analyticService } from 'global/services';

import formatPrice from 'app/utils/price';
import { getDishesFromBasketSections } from 'app/utils/dish';
import {
    pluralizeDishes, pluralizeDishesEn, pluralizeSets, pluralizeSetsEn,
} from 'app/utils/pluralize';
import { getDigitsFromPhone } from 'app/utils/phone';

import { LOCATION_MSK } from 'app/const/location';
import { Mobile, Desktop } from 'app/components/Responsive';
import { DialogAnimated } from 'app/components/Dialog';
import { LocaleContext } from 'app/containers/LocaleProvider';
import { LocationContext } from 'app/containers/LocationProvider';
import { useReadyToEatContradictionCheck } from 'app/views/Checkout/hooks/useReadyToEatContradictionCheck';
import { readyToEatDialogLocales } from 'app/views/Checkout/StepByStepCheckout/SingleStepCheckout/single-step-checkout-locales';

// TODO: useMutation
import { boxPages, boxDishes } from 'global/const/pagesCategories/boxes';
import { mutateTrialDOMSalesNodes } from '../../../../../trial/src/scripts/newTrialPage';
import pushLead from '../../../../../common/api/pushLead';

import { useLoadingCompleteHandler } from '../LoadigPage/LoadingContainer';

import { ConfigContext } from '../ConfigProvider';
import { BasketContext } from '../BasketProvider';
import { UserContext } from '../UserProvider';
import { FiltersContext } from '../FiltersProvider';

import { OpenBasketButtonTransition } from '../OpenBasketButton';
import CheckoutLinks from '../CheckoutLinks';
import DishCategories from '../DishCategories';
import { DishDetailsContainer } from '../DishDetailsContainer';
import { OrderDetails } from '../OrderDetails';

import { locales } from './order.locales';

import './order.scss';

const sendLead = async (phone) => {
    const pagePathSegment = window.location.pathname.slice(1, window.location.pathname.length - 1);

    function getValueFromUrl(key) {
        if (!window || !window.location) return '';

        const parsedUrl = new URL(window.location);
        const value = parsedUrl.searchParams.get(key);

        return value || '';
    }

    const options = {
        formName: pagePathSegment,
        phone: getDigitsFromPhone(phone),
        sourceSurvey: pagePathSegment,
        requestQuestion: pagePathSegment,
        referrerCode: getValueFromUrl('eeref'),
    };
    try {
        return await pushLead(options);
    } catch (e) {
        Raven.captureException(e);
        return e;
    }
};

const scrollToMenu = () => {
    const targetMenuNode = document.querySelector('#marchgiftsMenu');
    if (targetMenuNode) {
        targetMenuNode.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }
};


function Order({
    dialogType = 'OLD_MODAL',
}) {
    useLoadingCompleteHandler();

    const { locale } = useContext(LocaleContext);

    const {
        period,
        visibilityState: periodVisibilityState,
    } = useContext(FiltersContext);

    const {
        pageId,
        isCheckoutEnabled,
        isLinkToMenuEnabled,
        areDetailsEnabled,
        canChangePortionsCount,
        isCheckoutTitleHidden,
        isOpenBasketButtonHidden: isOpenBasketButtonDisabled,
        openBasketButtonType,
        dishesListType,
        orderDetailsType,
    } = useContext(ConfigContext);

    const {
        basket,
        promocodeConditions,
        updateBasket,
    } = useContext(BasketContext);
    const { isAuthed, userQuery } = useContext(UserContext);
    const { locationKey, isLocationDataLoading } = useContext(LocationContext);
    const [selectedLocationKey, setSelectedLocationKey] = useState(null);

    const [isOpenBasketButtonHidden, setIsOpenBasketButtonHidden] = useState(true);

    const activeLocationKey = selectedLocationKey || locationKey;

    useEffect(() => {
        setSelectedLocationKey(null);
    }, [locationKey]);

    const [intialCheckoutPhone, setInitialCheckoutPhone] = useState('');
    const [hasInvoice, setHasInvoice] = useState(false);
    const [isReadyToEatDishesRemoving, setIsReadyToEatDishesRemoving] = useState(false);
    const isLeadPushedRef = useRef(false);

    const containerRef = useRef(null);

    const {
        isReadyToEatDialogShown,
        setIsReadyToEatDialogShown,
        readyToEatDishes,
    } = useReadyToEatContradictionCheck(basket);

    const isStandardCheckout = orderDetailsType !== 'receipt' && orderDetailsType !== 'trial';
    const hasReadyToEatContradiction = (
        isCheckoutEnabled
        && isStandardCheckout
        && activeLocationKey !== LOCATION_MSK
        && readyToEatDishes.length > 0
    );

    const scrollToBasket = useCallback(() => {
        containerRef.current.scrollIntoView({
            // behavior: 'smooth',
            block: 'start',
        });
    }, []);

    const handleRemoveReadyToEatDishes = useCallback(async () => {
        if (isReadyToEatDishesRemoving) return;

        const dishIds = Array.from(new Set(
            readyToEatDishes.map(({ dish_id: dishId }) => String(dishId)),
        ));

        if (!dishIds.length) {
            setIsReadyToEatDialogShown(false);
            return;
        }

        setIsReadyToEatDishesRemoving(true);

        try {
            await dishIds.reduce(
                (updatePromise, dishId) => updatePromise.then(() => updateBasket({
                    dishId,
                    portions: 0,
                })),
                Promise.resolve(),
            );
            setIsReadyToEatDialogShown(false);
        } catch (error) {
            Raven.captureException(error);
        } finally {
            setIsReadyToEatDishesRemoving(false);
        }
    }, [isReadyToEatDishesRemoving, readyToEatDishes, setIsReadyToEatDialogShown, updateBasket]);

    const handleGoToAddressStep = useCallback(() => {
        if (isReadyToEatDishesRemoving) return;
        setIsReadyToEatDialogShown(false);

        const addressInput = document.getElementById('street');
        if (!addressInput) return;

        addressInput.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
        addressInput.focus();
    }, [isReadyToEatDishesRemoving, setIsReadyToEatDialogShown]);

    useEffect(() => {
        if (!isCheckoutEnabled || !isStandardCheckout || isLocationDataLoading) return;

        setIsReadyToEatDialogShown(hasReadyToEatContradiction);
    }, [isCheckoutEnabled, isStandardCheckout, isLocationDataLoading, hasReadyToEatContradiction, setIsReadyToEatDialogShown]);

    const sendLeadAndTrack = async (phone) => {
        try {
            const resp = await sendLead(phone);
            const { data: { pushLead: leadData } } = resp;

            analyticService.push({
                eventName: 'Submit_Phone_Number',
                source: pageId,
                userType: leadData.isNewbie ? 'new' : 'old',
            });
        } catch (e) {
            Raven.captureException(e);
        }
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const callback = ([entry]) => {
            const phone = intialCheckoutPhone || (isAuthed ? userQuery.data.user.phone : null);

            if (entry.isIntersecting) {
                if (openBasketButtonType === 'scrollToCheckout') {
                    setIsOpenBasketButtonHidden(true);
                }

                const { sections, totals, typeOfSet } = basket;
                analyticService.push({
                    eventName: 'Track_Navigate',
                    step: 2,
                    sections,
                    totals,
                    typeOfSet,
                }).push({
                    eventName: 'CityAds_Basket_Navigate',
                    sections,
                });

                if (phone && phone.length >= 11 && !isLeadPushedRef.current) {
                    sendLeadAndTrack(phone);
                    isLeadPushedRef.current = true;
                }
            } else {
                setIsOpenBasketButtonHidden(false);
            }
        };
        const observer = new IntersectionObserver(callback);
        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, [
        /**
        * Зависимости специально перечислены не все, чтобы эффект вызывался нужное количество раз
        */
        intialCheckoutPhone,
    ]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const basketDishes = useMemo(() => getDishesFromBasketSections(basket.sections), [basket.sections]);
    const basketDishesIds = useMemo(
        () => basketDishes.map((dish) => dish.id),
        [basketDishes],
    );
    const allDishesInBasketAreBoxes = basketDishesIds.every((dishId) => boxDishes.includes(dishId));
    const isBoxPage = boxPages.includes(pageId);

    const dishesCount = basketDishes.length;

    const price = basket.totals.total_common_price;
    const priceText = formatPrice(price);

    const buttonsDisabled = hasInvoice || !canChangePortionsCount;

    const originalPrice = basket.totals.total_price + basket.totals.delivery_price;

    const containerClasses = classNames({
        'order-container': true,
        [`order-container--${orderDetailsType}`]: Boolean(orderDetailsType),
        'order-container--boxes': isBoxPage,
    });

    const isLinksContainer = !isCheckoutTitleHidden && dishesCount > 0;
    const isMarchgiftsPage = pageId === 'marchgifts';

    const discountPercent = useMemo(
        () => Math.round((price / originalPrice) * 100),
        [originalPrice, price],
    );

    useEffect(
        () => {
            mutateTrialDOMSalesNodes({
                originalPrice,
                discountPercent,
            });
        },
        [originalPrice, discountPercent],
    );

    return (
        <div
            styleName={containerClasses}
            ref={containerRef}
        >
            {isLinksContainer && !isBoxPage && (
                <div styleName="ny__order-links-wrap">
                    <p styleName="ny__order-content__title text-container">
                        {locales[locale].cartTitle}
                        <Mobile><br /></Mobile>
                        <Desktop>&nbsp;</Desktop>
                        <span styleName="num">{dishesCount}</span>
                        &nbsp;
                        {!allDishesInBasketAreBoxes
                            && (locale === 'en'
                                ? pluralizeDishesEn(dishesCount)
                                : pluralizeDishes(dishesCount))}
                        {allDishesInBasketAreBoxes
                            && (locale === 'en'
                                ? pluralizeSetsEn(dishesCount)
                                : pluralizeSets(dishesCount))}
                        <Mobile><br /></Mobile>
                        <Desktop>&nbsp;</Desktop>
                        {locales[locale].cartPricePrefix}
                        &nbsp;
                        <span styleName="num">{priceText}</span>
                        &nbsp;₽
                    </p>
                    <CheckoutLinks
                        locale={locale}
                        period={period}
                        periodVisibilityState={periodVisibilityState}
                        isCheckoutEnabled={isCheckoutEnabled}
                        isLinkToMenuEnabled={isLinkToMenuEnabled}
                    />
                </div>
            )}

            {isCheckoutEnabled && (
                <OrderDetails
                    sendLeadAndTrack={sendLeadAndTrack}
                    setInitialCheckoutPhone={setInitialCheckoutPhone}
                    setHasInvoice={setHasInvoice}
                    basketDishes={basketDishes}
                    buttonsDisabled={buttonsDisabled}
                    hasReadyToEatContradiction={hasReadyToEatContradiction}
                    onLocationChange={setSelectedLocationKey}
                    phoneValue={(isAuthed ? userQuery.data.user.phone : null) || intialCheckoutPhone}
                />
            )}

            {dishesListType !== 'small' && (
                <DishCategories
                    buttonsDisabled={buttonsDisabled}
                    basketDishes={basketDishes}
                    scrollToBasket={scrollToBasket}
                />
            )}

            {areDetailsEnabled && (
                <DishDetailsContainer
                    basketDishes={basketDishes}
                    buttonsDisabled={buttonsDisabled}
                    dialogType={dialogType}
                />
            )}

            {!isOpenBasketButtonDisabled && !isMarchgiftsPage && (
                <OpenBasketButtonTransition
                    isHidden={isOpenBasketButtonHidden || dishesCount === 0}
                    onClick={scrollToBasket}
                    priceValue={price}
                    originalPrice={originalPrice}
                    promocodeConditions={promocodeConditions}
                    position="fixed"
                />
            )}
            {isMarchgiftsPage && (
                <OpenBasketButtonTransition
                    isHidden={false}
                    onClick={isOpenBasketButtonHidden || dishesCount === 0
                        ? scrollToMenu
                        : scrollToBasket
                    }
                    title={isOpenBasketButtonHidden || dishesCount === 0
                        ? 'Выбрать подарок'
                        : 'Оформить заказ'
                    }
                    priceValue={price}
                    originalPrice={originalPrice}
                    promocodeConditions={promocodeConditions}
                />
            )}
            <Portal>
                <DialogAnimated
                    {...readyToEatDialogLocales[locale]}
                    isOpen={Boolean(isReadyToEatDialogShown)}
                    isConfirmLoading={isReadyToEatDishesRemoving}
                    rejectDisabled={isReadyToEatDishesRemoving}
                    onConfirm={handleRemoveReadyToEatDishes}
                    onReject={handleGoToAddressStep}
                    rejectSpecificHandler={handleGoToAddressStep}
                />
            </Portal>
        </div>
    );
}

Order.propTypes = {
    dialogType: PropTypes.string,
};

const OrderWrapper = (props) => {
    const checkoutWrapperRef = useRef();

    const {
        pageId,
    } = useContext(ConfigContext);

    useEffect(() => {
        if (pageId === 'halloween') {
            const btnNode = document.querySelector('#byBoxButton');
            btnNode.addEventListener('click', () => {
                checkoutWrapperRef?.current?.scrollIntoView({
                    block: 'center',
                    behavior: 'smooth',
                });
            });
        }
    }, [pageId]);

    return (
        <div ref={checkoutWrapperRef}>
            <Order {...props} />
        </div>
    );
};

export default React.memo(OrderWrapper);

